import { useState, useEffect } from 'react'
import { useTheme, MANRINA_COLORS } from '../theme'
import { api, formatDate } from '../api'
import ThemeToggle from './ThemeToggle'
import { MVP } from '../mvp'
import Fox from './Fox'

// ============================================================
// Couches d'ambiance « spatiale » du prototype v2 (DESIGN-SYSTEM.md §4)
// Mouvements aleatoires : chaque element a ses propres --dx/--dy/--rot,
// duree et delai → jamais synchronises.
// ============================================================
const PARTICULES = [
  { e: '🥭', t: 12, l: 78, s: 30, dx: 26, dy: -34, rot: 14, d: 9, dl: 0 },
  { e: '🍤', t: 22, l: 14, s: 26, dx: -30, dy: 22, rot: -18, d: 12.5, dl: 1.1 },
  { e: '🧀', t: 30, l: 30, s: 24, dx: 18, dy: 30, rot: 10, d: 7, dl: 0.5 },
  { e: '🔬', t: 16, l: 60, s: 22, dx: -22, dy: -18, rot: -8, d: 14, dl: 1.6 },
  { e: '🌺', t: 38, l: 70, s: 26, dx: 34, dy: 16, rot: 22, d: 8, dl: 0.8 },
  { e: '🍯', t: 28, l: 50, s: 24, dx: -16, dy: 28, rot: -12, d: 11, dl: 2 },
  { e: '🍇', t: 46, l: 12, s: 28, dx: 28, dy: -20, rot: 16, d: 6.5, dl: 0.3 },
  { e: '🥕', t: 56, l: 8, s: 24, dx: 20, dy: 26, rot: -20, d: 13, dl: 1.4 },
  { e: '🥥', t: 60, l: 82, s: 28, dx: -28, dy: -22, rot: 12, d: 7.5, dl: 0.9 },
  { e: '🐟', t: 70, l: 60, s: 24, dx: 32, dy: 18, rot: -16, d: 12, dl: 1.8 },
  { e: '🍌', t: 66, l: 38, s: 22, dx: -18, dy: -30, rot: 18, d: 6.8, dl: 0.6 },
  { e: '🥬', t: 50, l: 88, s: 28, dx: -34, dy: 14, rot: -10, d: 10.5, dl: 1.2 },
  { e: '🎨', t: 78, l: 22, s: 22, dx: 22, dy: -24, rot: 20, d: 8.5, dl: 2.2 },
  { e: '🧺', t: 74, l: 78, s: 24, dx: -24, dy: 24, rot: -14, d: 13.5, dl: 0.4 },
  { e: '🍅', t: 84, l: 50, s: 22, dx: 16, dy: -28, rot: 12, d: 7.2, dl: 1.5 },
  { e: '🌿', t: 18, l: 44, s: 22, dx: -20, dy: 26, rot: -22, d: 11.5, dl: 1 },
]
const ETINCELLES = [
  { t: 20, l: 24, dx: 30, dy: 40, c: MANRINA_COLORS.orange, d: 6, dl: 0 },
  { t: 36, l: 84, dx: -40, dy: 26, c: 'rgb(var(--c-teal))', d: 8, dl: 1.4 },
  { t: 52, l: 30, dx: 24, dy: -36, c: 'rgb(var(--c-teal))', d: 7, dl: 0.6 },
  { t: 64, l: 70, dx: -30, dy: -28, c: MANRINA_COLORS.orange, d: 9, dl: 2.1 },
  { t: 30, l: 66, dx: 36, dy: 30, c: MANRINA_COLORS.orange, d: 6.5, dl: 0.9 },
  { t: 76, l: 46, dx: -26, dy: 34, c: 'rgb(var(--c-teal))', d: 8.5, dl: 1.7 },
  { t: 12, l: 50, dx: 20, dy: 44, c: 'rgb(var(--c-teal))', d: 7.5, dl: 0.3 },
  { t: 58, l: 16, dx: 40, dy: -22, c: MANRINA_COLORS.orange, d: 9.5, dl: 1.1 },
  { t: 44, l: 92, dx: -34, dy: -30, c: 'rgb(var(--c-teal))', d: 6.8, dl: 2.4 },
  { t: 88, l: 30, dx: 28, dy: -40, c: MANRINA_COLORS.orange, d: 8.2, dl: 0.5 },
]
const TRAILS = [
  { t: 24, l: -10, tx: 280, ty: 120, ang: 23, c: MANRINA_COLORS.orange, d: 7, dl: 1.2 },
  { t: 60, l: 110, tx: -300, ty: 90, ang: 197, c: 'rgb(var(--c-teal))', d: 9, dl: 3.5 },
  { t: 80, l: -10, tx: 320, ty: -120, ang: -21, c: 'rgb(var(--c-teal))', d: 8, dl: 6 },
  { t: 8, l: 100, tx: -260, ty: 160, ang: 148, c: MANRINA_COLORS.orange, d: 10, dl: 4.4 },
]

function ImmersiveScreen({ onExplore, onClose, onNavigate, isAuthenticated, user, marches }) {
  const { isWeb3 } = useTheme()
  // Repli : si le parent ne fournit pas les marches, on les charge ici (retrocompat).
  const [localMarches, setLocalMarches] = useState(null)
  useEffect(() => {
    if (marches !== undefined) return
    api.getMarches()
      .then(data => setLocalMarches(Array.isArray(data) ? data : []))
      .catch(() => setLocalMarches([]))
  }, [marches])

  const effective = marches !== undefined ? marches : localMarches
  const loading = effective == null
  const prochainMarche = Array.isArray(effective) ? effective[0] : null

  // Entree dans le hub : INSTANTANEE. Le bouton ne depend JAMAIS du reseau
  // (le prototype fait simplement setIntro(false)). On passe la marche si dispo.
  const handleExplore = () => onExplore(prochainMarche)

  const textPrimary = 'text-ink'
  const textSecondary = isWeb3 ? 'text-white/60' : 'text-ink/70'
  const textMuted = 'text-ink/60'

  // Particules Web3 (heritage)
  const particlesWeb3 = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    left: `${5 + (i * 6.5)}%`,
    delay: `${i * 0.4}s`,
    color: i % 2 === 0 ? MANRINA_COLORS.orange : MANRINA_COLORS.vert,
  }))

  return (
    <div className={`fixed inset-0 z-[70] ${isWeb3 ? 'portal-bg' : 'portal-bg-web2'}`}>
      {/* Fond + ambiance */}
      {isWeb3 ? (
        <>
          <div className="portal-bg-gradient" />
          <div className="portal-grid" />
          <div className="particles">
            {particlesWeb3.map(p => (
              <div key={p.id} className="particle" style={{ left: p.left, animationDelay: p.delay, backgroundColor: p.color }} />
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="portal-halos">
            <div className="portal-halo portal-halo-1" />
            <div className="portal-halo portal-halo-2" />
          </div>

          {/* Trainees filantes (effet spatial, discret) */}
          {TRAILS.map((tr, i) => (
            <span key={`t${i}`} aria-hidden style={{
              position: 'absolute', top: `${tr.t}%`, left: `${tr.l}%`, width: 46, height: 2, borderRadius: 99,
              background: `linear-gradient(90deg, transparent, ${tr.c}, transparent)`, transform: `rotate(${tr.ang}deg)`,
              '--tx': `${tr.tx}px`, '--ty': `${tr.ty}px`, animation: `shoot ${tr.d}s linear ${tr.dl}s infinite`,
              pointerEvents: 'none', zIndex: 1, opacity: 0,
            }} />
          ))}

          {/* Emojis nourriture flottants — mouvement aleatoire */}
          {PARTICULES.map((p, i) => (
            <span key={`p${i}`} aria-hidden style={{
              position: 'absolute', top: `${p.t}%`, left: `${p.l}%`, fontSize: p.s, opacity: 0.9,
              filter: 'drop-shadow(0 6px 10px rgba(7,62,61,0.12))',
              '--dx': `${p.dx}px`, '--dy': `${p.dy}px`, '--rot': p.rot,
              animation: `floatRand ${p.d}s ease-in-out ${p.dl}s infinite`, pointerEvents: 'none', zIndex: 1,
            }}>{p.e}</span>
          ))}

          {/* Etincelles errantes */}
          {ETINCELLES.map((s, i) => (
            <span key={`e${i}`} aria-hidden style={{
              position: 'absolute', top: `${s.t}%`, left: `${s.l}%`, width: 6, height: 6, borderRadius: '50%',
              background: s.c, boxShadow: `0 0 8px ${s.c}`,
              '--dx': `${s.dx}px`, '--dy': `${s.dy}px`, animation: `spark ${s.d}s ease-in-out ${s.dl}s infinite`,
              pointerEvents: 'none', zIndex: 1, opacity: 0,
            }} />
          ))}
        </>
      )}

      {/* (Bouton « Se connecter / avatar » en haut a droite retire — fidele a l'intro
         du prototype v4 qui n'a aucun controle en tete. La connexion se fait via le hub.) */}

      {MVP.THEME_TOGGLE && <ThemeToggle />}

      {/* Contenu centre */}
      <div className="fixed inset-0 flex flex-col items-center justify-center px-7 z-[71]">
        {isWeb3 ? (
          <>
            <div className="seed-of-life cursor-pointer transition-transform duration-300 hover:scale-105" onClick={() => { onClose() }}>
              <div className="seed-glow" />
              <svg viewBox="0 0 200 200">
                <circle className="seed-circle" cx="100" cy="100" r="40" style={{ stroke: MANRINA_COLORS.orange }} />
                <circle className="seed-circle" cx="100" cy="60" r="40" style={{ stroke: MANRINA_COLORS.vert, opacity: 0.5 }} />
                <circle className="seed-circle" cx="134.6" cy="80" r="40" style={{ stroke: MANRINA_COLORS.creme, opacity: 0.4 }} />
                <circle className="seed-circle" cx="134.6" cy="120" r="40" style={{ stroke: MANRINA_COLORS.orange, opacity: 0.5 }} />
                <circle className="seed-circle" cx="100" cy="140" r="40" style={{ stroke: MANRINA_COLORS.vert, opacity: 0.4 }} />
                <circle className="seed-circle" cx="65.4" cy="120" r="40" style={{ stroke: MANRINA_COLORS.creme, opacity: 0.5 }} />
                <circle className="seed-circle" cx="65.4" cy="80" r="40" style={{ stroke: MANRINA_COLORS.orange, opacity: 0.6 }} />
              </svg>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center text-center z-10">
                <span className="portal-fox text-6xl mb-2">🦊</span>
                <span className="text-white font-bold text-sm tracking-[2px]">RINA</span>
              </div>
            </div>
          </>
        ) : (
          /* Renard dans un orbe de verre sur un blob morphant + halo qui respire
             (DESIGN-SYSTEM.md §4 — port fidele du prototype v2) */
          <div
            className="relative cursor-pointer transition-transform duration-300 hover:scale-105"
            onClick={() => { onClose() }}
            style={{ width: 150, height: 150 }}
          >
            <div aria-hidden style={{
              position: 'absolute', inset: -18, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(244,137,83,0.27), transparent 70%)',
              animation: 'haloPulse 4s ease-in-out infinite',
            }} />
            <div aria-hidden style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(135deg, rgba(244,137,83,0.32), rgba(20,184,166,0.26))',
              animation: 'blobMorph 12s ease-in-out infinite', filter: 'blur(2px)',
            }} />
            <div className="glass-surface" style={{
              position: 'absolute', inset: 10, borderRadius: '46% 54% 52% 48% / 50% 50% 50% 50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              animation: 'foxBob 5s ease-in-out infinite',
            }}>
              <Fox size={92} />
            </div>
          </div>
        )}

        {/* RINA + salutation (serif, comme le prototype) */}
        <p className={`font-serif mt-[22px] ${textPrimary}`} style={{ fontSize: 30, letterSpacing: 1 }}>RINA</p>
        <p className={`font-serif mt-0.5 ${textPrimary}`} style={{ fontSize: 23 }}>
          {isAuthenticated && user?.prenom ? `Salut ${user.prenom} ! 👋` : 'Salut ! 👋'}
        </p>

        {/* Carte verre — prochain marche + CTA (DESIGN-SYSTEM.md §4) */}
        <div className="glass-surface rounded-3xl px-5 py-5 mt-[22px] text-center w-full max-w-xs">
          {loading ? (
            <div className="animate-pulse">
              <div className={`h-7 w-44 bg-ink/10 rounded mb-2 mx-auto`} />
              <div className={`h-4 w-32 bg-ink/5 rounded mx-auto`} />
            </div>
          ) : prochainMarche ? (
            <>
              <p className={`text-[10.5px] uppercase tracking-[2.5px] font-extrabold ${isWeb3 ? textMuted : 'text-primary'}`}>Prochain marché</p>
              <h3
                className="font-serif text-[22px] mt-1.5"
                style={{
                  color: isWeb3 ? 'transparent' : MANRINA_COLORS.vert,
                  backgroundImage: isWeb3 ? `linear-gradient(to right, white, ${MANRINA_COLORS.orange})` : 'none',
                  backgroundClip: isWeb3 ? 'text' : 'unset',
                  WebkitBackgroundClip: isWeb3 ? 'text' : 'unset',
                }}
              >
                {prochainMarche.lieu || prochainMarche.nom || 'Prochain Marché'}
              </h3>
              <p className={`text-[12.5px] ${textSecondary} mt-1`}>
                {formatDate(prochainMarche.date)}
                {prochainMarche.heureDebut && prochainMarche.heureFin && (
                  <span className="font-semibold" style={{ color: MANRINA_COLORS.orange }}> · {prochainMarche.heureDebut} – {prochainMarche.heureFin}</span>
                )}
              </p>
              {prochainMarche.participantsCount > 0 && (
                <div className="flex items-center justify-center gap-1.5 mt-2">
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'rgb(var(--c-teal))', animation: 'pulse 2s ease-in-out infinite' }} />
                  <p className={`text-[11.5px] ${textMuted}`}>
                    <span className="font-semibold" style={{ color: MANRINA_COLORS.orange }}>{prochainMarche.participantsCount}</span> producteurs vous attendent
                  </p>
                </div>
              )}
            </>
          ) : (
            /* Aucun marche programme : sans invitation explicite, l'ecran est un
               cul-de-sac (le raccourci « Voir le marche » ne mene nulle part).
               On dit ce qui se passe et on pousse vers le portail. */
            <>
              <h3
                className="font-serif text-2xl"
                style={{
                  color: isWeb3 ? 'transparent' : MANRINA_COLORS.vert,
                  backgroundImage: isWeb3 ? `linear-gradient(to right, white, ${MANRINA_COLORS.orange})` : 'none',
                  backgroundClip: isWeb3 ? 'text' : 'unset',
                  WebkitBackgroundClip: isWeb3 ? 'text' : 'unset',
                }}
              >
                Lyannaj by Manrina
              </h3>
              <p className={`text-[12.5px] ${textSecondary} mt-2 leading-relaxed`}>
                Pas de marché programmé pour le moment.
              </p>
              <p className={`text-[12px] ${textMuted} mt-1.5 leading-relaxed`}>
                Les producteurs, les paniers et leurs produits vous attendent à l'intérieur.
              </p>
            </>
          )}
        </div>

        {/* CTA principal — entre dans l'ACCUEIL (decouverte large) ; entree INSTANTANEE
            (onClose place sur l'onglet Accueil cote App). */}
        <button
          onClick={onClose}
          className="mt-[18px] w-full max-w-xs text-white"
          style={{
            padding: '17px 0', borderRadius: 99, border: 'none',
            background: `linear-gradient(120deg, ${MANRINA_COLORS.orange}, #e0653a 60%, ${MANRINA_COLORS.vert})`,
            fontWeight: 800, fontSize: 15, letterSpacing: 0.5,
            boxShadow: '0 18px 40px rgba(244,137,83,0.42)',
          }}
        >
          {prochainMarche ? 'Entrer dans Lyannaj →' : 'Découvrir Lyannaj →'}
        </button>

        {/* Raccourci direct vers le marche, juste sous le bouton principal.
            Masque quand aucun marche n'est programme : il n'ouvrait rien. */}
        {prochainMarche && (
          <button
            onClick={handleExplore}
            className={`mt-3 text-[13px] font-bold ${textSecondary}`}
          >
            🎪 Voir l'évènement →
          </button>
        )}

        {/* Visiteur : se connecter (prototype v5) */}
        {!isAuthenticated && (
          <button
            onClick={() => onNavigate('connexion')}
            className={`mt-2 text-[12.5px] font-semibold ${textMuted}`}
          >
            J'ai déjà un compte · Se connecter
          </button>
        )}
      </div>
    </div>
  )
}

export default ImmersiveScreen
